import { Injectable } from '@angular/core';
import { tap } from 'rxjs';

import { environment } from '@environments/environment.dev';
import {
    IReqChangePassword,
    IResVerifyOtp,
    IReqLoginUser,
    IResLoginUser,
    IResRefreshToken,
    IReqForgotPassword,
} from '@shared/model/auth.model';
import { HttpService } from '@services/http.service';
import { LOCAL_STORAGE_KEY, StorageService } from '@services/storage.service';
import { PermissionService } from '@services/permission.service';
import { ROUTE_PATH } from '@shared/enum/common.enum';
import { Router } from '@angular/router';
import { IUserPermissions } from '@shared/model/permission.model';
import { IResNever } from '@shared/model/common.model';
import { IndexedDBService } from './indexed-db.service';

const API_URL = `${environment.apiEndpoint}auth/`;

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    constructor(
        private readonly httpService: HttpService,
        private readonly storageService: StorageService,
        private readonly permissionService: PermissionService,
        private readonly router: Router,
        private readonly indexedDBService: IndexedDBService
    ) {}

    /**
     * Login user with email and password
     */
    login(body: IReqLoginUser) {
        this.storageService.clearStorage();
        return this.httpService
            .post<IResLoginUser>(`${API_URL}login`, body)
            .pipe(
                tap({
                    next: (response) => {
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.TEMP_TOKEN,
                            response.data.tempToken
                        );
                    },
                })
            );
    }

    /**
     * Login user with temp token for OTP verification
     */
    verifyOTP(body: { otp: number }) {
        return this.httpService
            .post<IResVerifyOtp>(`${API_URL}verifyOTP`, body)
            .pipe(
                tap({
                    next: (response) => {
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.IS_PASSWORD,
                            String(response?.data?.user?.is_password_changed)
                        );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.ACCESS_TOKEN,
                            response.data.accessToken
                        );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.REFRESH_TOKEN,
                            response.data.refreshToken
                        );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.USER_ID,
                            JSON.stringify(response.data.user)
                        );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.ROLE,
                            JSON.stringify(response.data.userPermission)
                        );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.POSSIBLE_ROLES,
                            JSON.stringify(response.data.possibleRoles)
                        );
                        const permissions =
                            response.data.userPermission.permissions!.reduce(
                                (
                                    acc: IUserPermissions,
                                    { module_id, permission_types }
                                ) => {
                                    acc[module_id] = permission_types;
                                    return acc;
                                },
                                {}
                            );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.PERMISSIONS,
                            JSON.stringify(permissions)
                        );
                        this.storageService.removeItem(
                            LOCAL_STORAGE_KEY.TEMP_TOKEN
                        );
                        this.permissionService.setUserPermissions(permissions);

                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.SELECTED_LANGUAGE,
                            String(response?.data?.user?.language_id)
                        );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.PRESENTATIONS,
                            JSON.stringify(response?.data?.presentations)
                        );
                    },
                })
            );
    }

    /**
     * Resend OTP to the user
     */
    resendOtp() {
        return this.httpService.get<IResNever>(`${API_URL}resendotp`);
    }

    /**
     * Log out the user
     */
    logOut() {
        this.storageService.clearStorage();
        this.indexedDBService.deleteIndexedDB();
        this.router.navigate([ROUTE_PATH.LOGIN]);
    }

    /**
     * Check if the user is authenticated
     */
    isAuthenticated() {
        return this.storageService.getItem(LOCAL_STORAGE_KEY.USER_ID) !== null;
    }

    /**
     * Refresh the access token using the refresh token
     * This method sends a request to the server to get a new access token when the current access token is expired.
     */
    refreshToken() {
        return this.httpService
            .post<IResRefreshToken>(`${API_URL}get-access-token`, {
                refreshToken: this.storageService.getItem(
                    LOCAL_STORAGE_KEY.REFRESH_TOKEN
                ),
            })
            .pipe(
                tap({
                    next: (response) => {
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.ACCESS_TOKEN,
                            response.data.accessToken
                        );
                    },
                })
            );
    }

    /**
     * Request to reset the password
     */
    forgotPassword(body: IReqForgotPassword) {
        return this.httpService.post<IResNever>(
            `${API_URL}forgotPassword`,
            body
        );
    }

    /**
     * Requests to change the password
     */
    changePassword(changePassword: IReqChangePassword) {
        return this.httpService.post<IResNever>(
            `${API_URL}changePassword`,
            changePassword
        );
    }

    /**
     * Selects the role for the user after login
     */
    selectRoleId(selected_role_id: number) {
        const body = {
            selected_role_id: selected_role_id,
        };
        return this.httpService
            .post<IResVerifyOtp>(`${API_URL}selectRole`, body)
            .pipe(
                tap({
                    next: (response) => {
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.IS_PASSWORD,
                            String(response?.data?.user?.is_password_changed)
                        );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.ACCESS_TOKEN,
                            response.data.accessToken
                        );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.REFRESH_TOKEN,
                            response.data.refreshToken
                        );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.USER_ID,
                            JSON.stringify(response.data.user)
                        );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.ROLE,
                            JSON.stringify(response.data.userPermission)
                        );

                        const permissions =
                            response.data.userPermission.permissions!.reduce(
                                (
                                    acc: IUserPermissions,
                                    { module_id, permission_types }
                                ) => {
                                    acc[module_id] = permission_types;
                                    return acc;
                                },
                                {}
                            );
                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.PERMISSIONS,
                            JSON.stringify(permissions)
                        );
                        this.permissionService.setUserPermissions(permissions);

                        this.storageService.setItem(
                            LOCAL_STORAGE_KEY.SELECTED_LANGUAGE,
                            String(response?.data?.user?.language_id)
                        );
                    },
                })
            );
    }
}
